/**
 * component-hero-slideshow.js -- Hero Slideshow Web Component for Mirai Online (Phase 4)
 *
 * <hero-slideshow> custom element:
 *  - Auto-advances slides at a configurable interval (data-interval, ms)
 *  - Respects prefers-reduced-motion: auto-advance is disabled when reduced motion is preferred
 *  - Pauses on dot/arrow interaction and resumes after 8000ms idle
 *  - Dot controls sync active state with the current slide
 *  - Prev/next arrow buttons cycle through slides
 *  - Teardown clears all timers to prevent stacked intervals across theme-editor edits
 *  - Reinitializes on document shopify:section:load (with containment self-check) for theme editor compatibility
 *  - Light DOM only (no Shadow DOM) so design tokens reach in
 *
 * Critical: The first slide's hero-slide--active class is server-rendered by Liquid.
 * This component NEVER sets the initial active slide — it only manages transitions
 * after the first user or timer advance. No display:none is applied to slides.
 */

/* ============================================================
   HERO SLIDESHOW WEB COMPONENT
   ============================================================ */

class HeroSlideshow extends HTMLElement {
  constructor() {
    super();

    // Stable bound references for add/removeEventListener symmetry
    this._boundDotClick = this._handleDotClick.bind(this);
    this._boundPrevClick = this._handlePrevClick.bind(this);
    this._boundNextClick = this._handleNextClick.bind(this);
    this._boundSectionLoad = null;
    this._boundEnter = () => { this._hovering = true; };
    this._boundLeave = () => { this._hovering = false; };
    this._boundFocusIn = () => { this._focused = true; };
    this._boundFocusOut = () => { this._focused = false; };
    this._boundToggleStopped = () => this._toggleStopped();
    this._boundVisibility = () => {
      this._tabHidden = document.visibilityState === 'hidden';
    };

    // State
    this._slides = [];
    this._dots = [];
    this._current = 0;
    this._timer = null;
    this._resumeTimeout = null;
    this._paused = false;
    this._hovering = false;
    this._focused = false;
    this._stopped = false;
    this._tabHidden = false;
    this._toggleBtn = null;
    this._interval = 5000;
  }

  connectedCallback() {
    this._init();
    // Theme editor reinit: Shopify fires shopify:section:load on the section wrapper
    // <div> and on document, NOT on this inner custom element, and it does not propagate
    // downward. Listen on document with a containment self-check so reinit fires only when
    // THIS component's section is the one being reloaded. (The theme.liquid forwarding
    // bridge targets [data-section-type], which no element sets — so it is a no-op here.)
    this._boundSectionLoad = (event) => {
      if (event.target && event.target.contains(this)) this._reinit();
    };
    document.addEventListener('shopify:section:load', this._boundSectionLoad);
  }

  disconnectedCallback() {
    if (this._boundSectionLoad) {
      document.removeEventListener('shopify:section:load', this._boundSectionLoad);
      this._boundSectionLoad = null;
    }
    this._teardown();
  }

  _init() {
    this._slides = Array.from(this.querySelectorAll('[data-slide]'));
    this._dots = Array.from(this.querySelectorAll('[data-slide-dot]'));

    // Read interval from data attribute (already multiplied by 1000 in Liquid)
    const rawInterval = parseInt(this.dataset.interval, 10);
    this._interval = Number.isFinite(rawInterval) && rawInterval > 0 ? rawInterval : 5000;

    // Determine current active slide index from server-rendered class
    const activeSlide = this.querySelector('.hero-slide--active');
    if (activeSlide) {
      const idx = parseInt(activeSlide.dataset.slide, 10);
      this._current = Number.isFinite(idx) ? idx : 0;
    } else {
      this._current = 0;
    }

    // Wire dot click handlers
    this._dots.forEach((dot) => {
      dot.addEventListener('click', this._boundDotClick);
    });

    // Wire prev/next arrow handlers
    const prevBtn = this.querySelector('[data-slide-prev]');
    const nextBtn = this.querySelector('[data-slide-next]');
    if (prevBtn) prevBtn.addEventListener('click', this._boundPrevClick);
    if (nextBtn) nextBtn.addEventListener('click', this._boundNextClick);

    // Suspend autoplay while the shopper is engaging with the hero (pointer over
    // it, or keyboard focus inside it) — otherwise the slide changes mid-read.
    this.addEventListener('mouseenter', this._boundEnter);
    this.addEventListener('mouseleave', this._boundLeave);
    this.addEventListener('focusin', this._boundFocusIn);
    this.addEventListener('focusout', this._boundFocusOut);

    this._toggleBtn = this.querySelector('[data-slide-playpause]');
    if (this._toggleBtn) this._toggleBtn.addEventListener('click', this._boundToggleStopped);

    // Don't keep advancing slides in a backgrounded tab.
    document.addEventListener('visibilitychange', this._boundVisibility);

    this._startAutoAdvance();
  }

  _teardown() {
    clearInterval(this._timer);
    clearTimeout(this._resumeTimeout);
    this._timer = null;
    this._resumeTimeout = null;
    this._paused = false;
    this._hovering = false;
    this._focused = false;

    this.removeEventListener('mouseenter', this._boundEnter);
    this.removeEventListener('mouseleave', this._boundLeave);
    this.removeEventListener('focusin', this._boundFocusIn);
    this.removeEventListener('focusout', this._boundFocusOut);
    document.removeEventListener('visibilitychange', this._boundVisibility);
    if (this._toggleBtn) this._toggleBtn.removeEventListener('click', this._boundToggleStopped);
  }

  _reinit() {
    this._teardown();
    this._init();
  }

  /**
   * Transition to a slide by index.
   *
   * @param {number}  index
   * @param {boolean} [userInitiated] - true for dot/arrow clicks. The idle-resume
   *   pause must fire ONLY for these. It used to run unconditionally, including
   *   when the autoplay timer itself called _goTo — so the first auto-advance set
   *   _paused for 8s, swallowing the next tick and silently ~doubling the
   *   merchant's interval (a 5s setting advanced every ~10s; 3s became ~9s).
   */
  _goTo(index, userInitiated = false) {
    if (!this._slides.length) return;

    const total = this._slides.length;
    const next = ((index % total) + total) % total;

    // Swap active class on slides
    if (this._slides[this._current]) {
      this._slides[this._current].classList.remove('hero-slide--active');
    }
    if (this._slides[next]) {
      this._slides[next].classList.add('hero-slide--active');
    }

    // Sync dot active state + aria-selected
    this._dots.forEach((dot, i) => {
      const isActive = i === next;
      dot.classList.toggle('hero-dot--active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this._current = next;
    if (userInitiated) this._pauseTemporarily();
  }

  /**
   * Start auto-advance interval.
   * Short-circuits if prefers-reduced-motion: reduce is set, or there is only one slide.
   */
  _startAutoAdvance() {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || this._slides.length <= 1) return;

    this._timer = setInterval(() => {
      // Hovering, keyboard focus inside the hero, a backgrounded tab, or an
      // explicit Pause all suspend advancing. Content that auto-updates for more
      // than 5s with no way to stop it is a WCAG 2.2.2 failure — and a slide
      // swapping out from under someone mid-sentence is just hostile.
      //
      // Tab visibility is tracked via the visibilitychange listener below rather
      // than read from document.hidden here: `hidden` is true for ANY non-visible
      // visibilityState, including 'prerender', so reading it directly would
      // suspend autoplay in environments that never reach 'visible'.
      if (this._paused || this._hovering || this._focused || this._stopped || this._tabHidden) return;
      this._goTo((this._current + 1) % this._slides.length);
    }, this._interval);
  }

  /**
   * Pause auto-advance temporarily after a USER interaction (dot/arrow).
   * Resumes after 8000ms idle (per UI-SPEC).
   */
  _pauseTemporarily() {
    this._paused = true;
    clearTimeout(this._resumeTimeout);
    this._resumeTimeout = setTimeout(() => {
      this._paused = false;
    }, 8000);
  }

  /** Explicit, sticky pause/play — the WCAG 2.2.2 control. */
  _toggleStopped() {
    this._stopped = !this._stopped;
    if (!this._toggleBtn) return;
    this._toggleBtn.setAttribute('aria-pressed', this._stopped ? 'true' : 'false');
    const label = this._stopped
      ? this._toggleBtn.dataset.strPlay || 'Play slideshow'
      : this._toggleBtn.dataset.strPause || 'Pause slideshow';
    this._toggleBtn.setAttribute('aria-label', label);
    this._toggleBtn.classList.toggle('hero-playpause--paused', this._stopped);
  }

  /* ---------- Event handlers ---------- */

  _handleDotClick(e) {
    const dot = e.currentTarget;
    const idx = parseInt(dot.dataset.slideDot, 10);
    if (Number.isFinite(idx)) {
      this._goTo(idx, true);
    }
  }

  _handlePrevClick() {
    const total = this._slides.length;
    this._goTo((this._current - 1 + total) % total, true);
  }

  _handleNextClick() {
    this._goTo((this._current + 1) % this._slides.length, true);
  }
}

// Registration guard: only define once even if the section renders multiple times
// or the script reloads in the theme editor.
if (!customElements.get('hero-slideshow')) {
  customElements.define('hero-slideshow', HeroSlideshow);
}
